import { useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { eventBus } from '@atc/cai-event-bus';

import { paymentsDuck } from '@/ducks';

const usePaymentData = (supportedDealTypes) => {
    const [hasError, setHasError] = useState(false);
    const [localPayments, setLocalPayments] = useState(null);
    const [paymentsData, setPaymentsData] = useState(null);
    const paymentsRef = useRef(localPayments);

    const isLoading = useSelector(paymentsDuck.selectors.getIsLoading);

    // setting error on timeout
    useEffect(() => {
        setTimeout(() => {
            if (paymentsRef.current && Object.keys(paymentsRef.current).length === 0) {
                setHasError(true);
            }
        }, 5000);
    }, []);

    const handlePaymentCalResponseUpdate = (paymentInfo) => {
        setLocalPayments(paymentInfo);
    };

    useEffect(() => {
        eventBus.on(
            'uds:PaymentCalcResponse:Update',
            handlePaymentCalResponseUpdate
        );

        return () => {
            eventBus.off(
                'uds:PaymentCalcResponse:Update',
                handlePaymentCalResponseUpdate
            );
        };
    }, []);

    useEffect(() => {
        // update ref to current value for timeout
        paymentsRef.current = localPayments;

        // update payments data
        const tradeInValue = localPayments?.tradeInValue ?? 0;
        const amountOwed = localPayments?.amountOwed ?? 0;
        if (localPayments && Object.keys(localPayments).length > 0) {
            const creditRatingKey = Object.keys(localPayments?.rate)[0];
            const paymentTypes = localPayments?.rate[creditRatingKey];

            const labels = ['finance', 'lease', 'cash'];
            const formattedPayments = labels.map((label) => {
                let key;
                if (label === 'finance') {
                    key = paymentTypes.paymentOffers ? 'paymentOffers' : 'nationalOffers';
                } else {
                    key = `${label}Offers`;
                }

                let loanTermKey = Object.keys(paymentTypes[key] || {})[0];
                let paymentLabel = label.charAt(0).toUpperCase() + label.slice(1);
                let paymentTypeData = paymentTypes[key]?.[loanTermKey]?.[0];

                // This is needed when the cashOffers object is not returned form the payment calculation service
                // and we need to calculate the cash fallback
                if (!paymentTypeData && label === 'cash' && supportedDealTypes?.includes('cash')) {
                    const cashFallbackKey = Object.keys(paymentTypes)[0];
                    loanTermKey = Object.keys(paymentTypes[cashFallbackKey] || {})[0];
                    paymentTypeData = paymentTypes[cashFallbackKey]?.[loanTermKey]?.[0];
                    paymentLabel = 'Cash';
                }

                if (!paymentTypeData) {
                    return { paymentLabel, paymentInfo: {}, isPaymentError: true };
                }

                const taxesAndFees = paymentTypeData?.taxesFees?.totalTaxesFees ?? 0;
                const salePrice = paymentTypeData?.pricingDetails?.salePrice ?? 0;
                const totalCashPayment = salePrice - tradeInValue + amountOwed + taxesAndFees;
                const payment = key === 'cashOffers' ? totalCashPayment : paymentTypeData?.payment;
                const incentiveIds = paymentTypeData?.incentiveIds;
                const loanTerm = loanTermKey !== '-1' ? Number(loanTermKey) : null;

                return {
                    payment: Math.floor(payment),
                    paymentLabel,
                    customRate: localPayments?.customRate,
                    isPaymentError: payment <= 0,
                    hasIncentive: incentiveIds !== undefined && incentiveIds.length !== 0,
                    paymentInfo: { ...paymentTypeData, loanTerm, taxesAndFees },
                };
            });

            setPaymentsData(formattedPayments);
        }
    }, [localPayments, supportedDealTypes]);

    return [isLoading, hasError, paymentsData];
};

export default usePaymentData;
